/**
 * Auth translations: auth, register, verifyEmail
 */

export const auth = {
  login: 'Log in',
  logout: 'Log out',
  register: 'Register',
  welcome: 'Welcome back',
  loginPrompt: 'Log in to your account',
  noAccount: "Don't have an account?",
  registerNow: 'Register now',
  alreadyHaveAccount: 'Already have an account?',
  loginNow: 'Log in now',
  email: 'Email address',
  emailPlaceholder: "name{'@'}example.com",
  password: 'Password',
  passwordPlaceholder: '••••••••',
  showPassword: 'Show password',
  hidePassword: 'Hide password',
  confirmCode: 'Confirmation code',
  otpHint: 'Enter the code from your authenticator app or email',
  otpPlaceholder: '123456',
  confirm: 'Confirm',
  loginSuccess: 'You have been successfully logged in.',
  loginFailed: 'Login failed',
  wrongCredentials: 'Email or password is incorrect.',
  tooManyAttempts: 'Too many login attempts. Please wait a moment.',
  sessionExpired: 'Session expired',
  sessionExpiredMessage: 'Please log in again.',
  verificationFailed: 'Verification failed',
  otpInvalid: 'The confirmation code is invalid or expired.',
  createAccount: 'Create account',
  registerPrompt: 'Register for your access',
  registerStep1: 'Personal details',
  registerStep2: 'Login credentials',
  registerSuccess: 'Your account has been successfully created.',
  registerFailed: 'Registration failed',
  emailAlreadyExists: 'This email address is already registered.',
  captchaFailed: 'Captcha verification failed. Please try again.',
  turnstileError: 'Verification could not load. If this keeps happening, allow challenges.cloudflare.com in your ad or tracking blocker.',
  turnstileErrorCode: 'Error code: {code}',
  turnstileRetry: 'Try again',
  invalidInput: 'Invalid input. Please check your details.',
  forgotPassword: 'Forgot password?',
  forgotPasswordTitle: 'Reset password',
  forgotPasswordPrompt: 'Enter your email address to receive a link to reset your password.',
  sendResetLink: 'Send link',
  resetLinkSent: 'Link sent',
  resetLinkSentMessage: 'If an account with this email exists, you will shortly receive an email with instructions to reset your password.',
  backToLogin: 'Back to login',
  resetPasswordTitle: 'Set new password',
  resetPasswordPrompt: 'Enter your new password.',
  newPassword: 'New password',
  confirmNewPassword: 'Confirm password',
  resetPassword: 'Reset password',
  passwordResetSuccess: 'Password successfully changed',
  passwordResetSuccessMessage: 'Your password has been successfully changed. You can now log in.',
  passwordResetFailed: 'Password reset failed',
  invalidResetToken: 'The link is invalid or expired. Please request a new link.',
  passwordsDoNotMatch: 'The passwords do not match.',
  noResetToken: 'No token found. Please open the link from the email.',
  requestNewLink: 'Request new link'
} as const

export const register = {
  accountType: {
    person: 'Individual',
    organization: 'Company'
  },
  fields: {
    orgName: 'Company name including legal form',
    vatId: 'VAT ID (optional)',
    vatIdHint: "We check the number in the EU Commission's VIES system. A German number is only checked for validity. For a number from another EU country we also send your company name and billing address to that country's tax administration; once confirmed, you pay net prices without German VAT. Details in the privacy policy.",
    firstName: 'First name',
    lastName: 'Last name',
    street: 'Street and house number',
    addressLine2: 'Address line 2 (optional)',
    country: 'Country',
    postcode: 'Postcode',
    state: 'County / Region',
    city: 'City',
    phonePrefix: 'Dialling code',
    phone: 'Mobile number',
    email: 'Email address',
    emailPlaceholder: "name{'@'}example.com",
    emailHint: 'This email will be used for login and communication',
    password: 'Create password',
    passwordPlaceholder: 'Choose a secure password'
  },
  sections: {
    personal: 'Personal details',
    address: 'Address',
    contact: 'Contact'
  },
  buttons: {
    next: 'Next',
    back: 'Back to step 1',
    createAccount: 'Create account'
  },
  errors: {
    passwordTooWeak: 'Password too weak',
    passwordTooWeakMessage: 'Please choose a stronger password with upper/lowercase letters and numbers.',
    invalidInput: 'Invalid input',
    checkInputs: 'Please check your entries.',
    captchaRequired: 'Captcha verification required. Please reload the page and try again.',
    invalidEmail: 'Please enter a valid email address.',
    invalidPassword: 'The password must contain at least 8 characters, upper/lowercase letters and a number.',
    passwordComplexity: 'Password must contain upper/lowercase letters and at least one number'
  },
  terms: {
    prefix: 'By registering, you accept our',
    agb: 'Terms and Conditions',
    and: 'and',
    privacy: 'Privacy Policy'
  }
} as const

export const verifyEmail = {
  loading: {
    title: 'Verifying email...',
    description: 'Please wait a moment.'
  },
  success: {
    title: 'Email verified!',
    description: 'Your email address has been successfully verified. You now have access to all features.',
    goToDashboard: 'Go to dashboard'
  },
  alreadyVerified: {
    title: 'Already verified',
    description: 'Your email address has already been verified.',
    goToDashboard: 'Go to dashboard'
  },
  error: {
    title: 'Verification failed',
    goToLogin: 'Go to login',
    goToDashboard: 'Go to dashboard'
  },
  errors: {
    noToken: 'No verification token found. Please open the link from the email again.',
    notLoggedIn: 'You must be logged in to verify your email. Please log in and then click the link again.',
    invalidToken: 'The verification link is invalid or expired. Please request a new verification email.',
    generic: 'An error occurred during verification. Please try again later.'
  }
} as const
